import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { RESOURCE_TYPES, PROJECT_STATUSES, STATUS_COLORS, PROJECT_TYPES, CATEGORIES } from '@/lib/constants';
import { calculateWeeks, getWeekLabels, getProjectDuration, formatCurrency } from '@/lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Save, FileText, CalendarIcon, Plus, Trash2, ArrowLeft, Clock, DollarSign, Download, Copy, Lock, Unlock, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import * as XLSX from 'xlsx';

const DEFAULT_RESOURCES = ['data_engineer', 'data_architect', 'project_manager', 'qa_testing'];

const createEmptyResource = (type) => {
  const resType = RESOURCE_TYPES.find(r => r.value === type) || RESOURCE_TYPES[0];
  return {
    type: resType.value,
    label: resType.label,
    rate: resType.defaultRate,
    weeklyAllocation: [],
  };
};

export default function EstimateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const cameFromHeatmap = location.state?.from === 'heatmap';

  const [project, setProject] = useState({
    id: '',
    clientName: '',
    projectName: '',
    startDate: '',
    endDate: '',
    projectLead: '',
    status: 'draft',
    projectType: 'staff_aug',
    category: 'regular',
    poNumber: '',
    resources: DEFAULT_RESOURCES.map(r => createEmptyResource(r)),
  });

  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [applyAllWeeks, setApplyAllWeeks] = useState({});
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [passkeyInput, setPasskeyInput] = useState('');
  const [passkeyAction, setPasskeyAction] = useState('unlock'); // 'lock' or 'unlock'
  const [passkeyError, setPasskeyError] = useState('');
  const [userUnlocked, setUserUnlocked] = useState(false);
  const [activeVersion, setActiveVersion] = useState(1);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [savingLoading, setSavingLoading] = useState(false);

  const isLockable = ['sow', 'execute'].includes(project.status);
  const isLocked = isLockable && !isNew && !userUnlocked && project.isLocked !== false;
  const isVersionable = ['draft', 'proposal'].includes(project.status);

  useEffect(() => {
    const loadData = async () => {
      if (!isNew) {
        try {
          const existing = await api.getProject(id);
          if (existing) {
            // Auto-initialize V1 for Draft/Proposal projects that have no versions yet
            if (['draft', 'proposal'].includes(existing.status) && (!existing.versions || existing.versions.length === 0)) {
              existing.versions = [{ version: 1, resources: (existing.resources || []).map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) }];
            }
            setProject(existing);
            if (existing.versions && existing.versions.length > 0) {
              setActiveVersion(existing.selectedVersion || existing.versions[0].version || 1);
            }
          } else {
            navigate('/');
          }
        } catch {
          navigate('/');
        }
      } else {
        try {
          const nextId = await api.getNextId();
          setProject(prev => ({ ...prev, id: nextId }));
        } catch {
          setProject(prev => ({ ...prev, id: `EST-${Date.now()}` }));
        }
      }
    };
    loadData();
  }, [id, isNew, navigate]);

  const numWeeks = calculateWeeks(project.startDate, project.endDate);
  const weekLabels = getWeekLabels(project.startDate, numWeeks);
  const duration = getProjectDuration(project.startDate, project.endDate);

  // Ensure all resources have correct allocation array length
  useEffect(() => {
    if (numWeeks > 0) {
      setProject(prev => ({
        ...prev,
        resources: prev.resources.map(res => {
          const current = res.weeklyAllocation || [];
          if (current.length !== numWeeks) {
            const newAlloc = Array(numWeeks).fill(0);
            current.forEach((val, i) => {
              if (i < numWeeks) newAlloc[i] = val;
            });
            return { ...res, weeklyAllocation: newAlloc };
          }
          return res;
        }),
      }));
    }
  }, [numWeeks]);

  const updateField = (field, value) => {
    // Intercept status change to SoW/Execute from Draft/Proposal when multiple versions exist
    if (field === 'status' && ['sow', 'execute'].includes(value) && ['draft', 'proposal'].includes(project.status)) {
      const vers = project.versions || [];
      if (vers.length > 1) {
        setPendingStatus(value);
        setShowVersionModal(true);
        return;
      }
    }
    setProject(prev => ({ ...prev, [field]: value }));
  };

  const updateResource = (idx, field, value) => {
    setProject(prev => {
      const resources = [...prev.resources];
      resources[idx] = { ...resources[idx], [field]: value };
      return { ...prev, resources };
    });
  };

  const updateAllocation = (resIdx, weekIdx, value) => {
    setProject(prev => {
      const resources = [...prev.resources];
      const alloc = [...(resources[resIdx].weeklyAllocation || [])];
      const numVal = Number(value) || 0;
      alloc[weekIdx] = numVal;
      // If "apply to all" is checked and week 1 (index 0) changed, propagate
      if (applyAllWeeks[resIdx] && weekIdx === 0) {
        for (let i = 1; i < alloc.length; i++) alloc[i] = numVal;
      }
      resources[resIdx] = { ...resources[resIdx], weeklyAllocation: alloc };
      return { ...prev, resources };
    });
  };

  const toggleApplyAll = (resIdx, checked) => {
    setApplyAllWeeks(prev => ({ ...prev, [resIdx]: checked }));
    if (checked) {
      setProject(prev => {
        const resources = [...prev.resources];
        const alloc = [...(resources[resIdx].weeklyAllocation || [])];
        const week1Val = alloc[0] || 0;
        for (let i = 1; i < alloc.length; i++) alloc[i] = week1Val;
        resources[resIdx] = { ...resources[resIdx], weeklyAllocation: alloc };
        return { ...prev, resources };
      });
    }
  };

  const changeResourceType = (resIdx, newType) => {
    const resType = RESOURCE_TYPES.find(r => r.value === newType);
    if (!resType) return;
    setProject(prev => {
      const resources = [...prev.resources];
      resources[resIdx] = {
        ...resources[resIdx],
        type: resType.value,
        label: resType.label,
        rate: resType.defaultRate,
      };
      return { ...prev, resources };
    });
  };

  const addResource = () => {
    const usedTypes = project.resources.map(r => r.type);
    const available = RESOURCE_TYPES.find(r => !usedTypes.includes(r.value));
    const newRes = createEmptyResource(available ? available.value : RESOURCE_TYPES[0].value);
    newRes.weeklyAllocation = Array(numWeeks).fill(0);
    setProject(prev => ({ ...prev, resources: [...prev.resources, newRes] }));
  };

  const removeResource = (idx) => {
    if (project.resources.length <= 1) return;
    setProject(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== idx),
    }));
  };

  // --- Version management ---
  const saveCurrentToVersions = (proj) => {
    const vers = [...(proj.versions || [])];
    const idx = vers.findIndex(v => v.version === activeVersion);
    const snapshot = { version: activeVersion, resources: proj.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) };
    if (idx >= 0) vers[idx] = snapshot;
    else vers.push(snapshot);
    return vers;
  };

  const createNewVersion = () => {
    const vers = saveCurrentToVersions(project);
    const nextNum = vers.length + 1;
    if (nextNum > 3) return;
    const newResources = project.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] }));
    vers.push({ version: nextNum, resources: newResources });
    setProject(prev => ({ ...prev, versions: vers, resources: newResources }));
    setActiveVersion(nextNum);
    toast.success(`Version V${nextNum} created`);
  };

  const switchVersion = (vNum) => {
    if (vNum === activeVersion) return;
    const vers = saveCurrentToVersions(project);
    const target = vers.find(v => v.version === vNum);
    if (target) {
      setProject(prev => ({ ...prev, versions: vers, resources: target.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })) }));
      setActiveVersion(vNum);
    }
  };

  const finalizeVersion = (vNum) => {
    const vers = saveCurrentToVersions(project);
    const target = vers.find(v => v.version === vNum);
    if (target) {
      setProject(prev => ({
        ...prev,
        status: pendingStatus,
        resources: target.resources.map(r => ({ ...r, weeklyAllocation: [...(r.weeklyAllocation || [])] })),
        versions: vers,
        selectedVersion: vNum,
      }));
    }
    setShowVersionModal(false);
    setPendingStatus(null);
    toast.success(`Version V${vNum} selected as final estimate`);
  };

  const getResourceTotal = (res) => {
    return (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0);
  };

  const getWeekTotal = (weekIdx) => {
    return project.resources.reduce((sum, res) => {
      return sum + ((res.weeklyAllocation || [])[weekIdx] || 0) * (res.rate || 0);
    }, 0);
  };

  const grandTotal = project.resources.reduce((sum, res) => sum + getResourceTotal(res), 0);

  const openPasskeyModal = (action) => {
    setPasskeyAction(action);
    setPasskeyInput('');
    setPasskeyError('');
    setShowPasskeyModal(true);
  };

  const handlePasskeySubmit = async () => {
    try {
      if (passkeyAction === 'lock') {
        await api.lockProject(project.id, passkeyInput);
        setProject(prev => ({ ...prev, isLocked: true }));
        setUserUnlocked(false);
        toast.success('Estimates locked successfully');
      } else {
        await api.unlockProject(project.id, passkeyInput);
        setProject(prev => ({ ...prev, isLocked: false }));
        setUserUnlocked(true);
        toast.success('Estimates unlocked for editing');
      }
      setShowPasskeyModal(false);
    } catch {
      setPasskeyError('Invalid passkey. Please try again.');
    }
  };

  const handleExportExcel = () => {
    if (numWeeks === 0) return;
    const data = project.resources.map(res => {
      const row = { 'Resource': res.label, 'Rate ($/wk)': res.rate };
      weekLabels.forEach((label, i) => { row[label] = (res.weeklyAllocation || [])[i] || 0; });
      row['Total ($)'] = getResourceTotal(res);
      return row;
    });
    // Add weekly totals row
    const totalsRow = { 'Resource': 'Weekly Total', 'Rate ($/wk)': '' };
    weekLabels.forEach((label, i) => { totalsRow[label] = getWeekTotal(i); });
    totalsRow['Total ($)'] = grandTotal;
    data.push(totalsRow);

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estimate');
    XLSX.writeFile(wb, `${project.id}_estimate.xlsx`);
    toast.success('Estimate table exported to Excel');
  };

  const handleSave = async () => {
    if (!project.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (project.status === 'execute' && !(project.poNumber || '').trim()) {
      toast.error('PO Number is required for Execute status. Assign a PO or use the dummy PO option.');
      return;
    }
    const versionsToSave = saveCurrentToVersions(project);
    const dataToSave = { ...project, versions: versionsToSave, selectedVersion: project.selectedVersion || activeVersion };
    try {
      setSavingLoading(true);
      if (isNew) {
        const saved = await api.createProject(dataToSave);
        setProject(saved);
      } else {
        const saved = await api.updateProject(project.id, dataToSave);
        setProject(saved);
      }
      toast.success('Estimate saved successfully');
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSavingLoading(false);
    }
  };

  const handleSaveAndViewInvoices = async () => {
    if (!project.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (!project.startDate || !project.endDate) {
      toast.error('Start and end dates are required for invoices');
      return;
    }
    if (project.status === 'draft' || project.status === 'proposal') {
      toast.error('Invoices cannot be created for projects in Draft or Proposal phase. Move the project to SoW or Execute status first.');
      return;
    }
    if (project.status === 'execute' && !(project.poNumber || '').trim()) {
      toast.error('PO Number is required for Execute status. Assign a PO or use the dummy PO option.');
      return;
    }
    const versionsToSave = saveCurrentToVersions(project);
    const dataToSave = { ...project, versions: versionsToSave, selectedVersion: project.selectedVersion || activeVersion };
    try {
      setSavingLoading(true);
      if (isNew) {
        await api.createProject(dataToSave);
      } else {
        await api.updateProject(project.id, dataToSave);
      }
      toast.success('Estimate saved');
      navigate(`/invoices/${project.id}`);
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSavingLoading(false);
    }
  };

  return (
    <div data-testid="estimate-form-page">
      <Toaster theme="dark" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cameFromHeatmap ? navigate('/', { state: { tab: 'resource-heatmap' } }) : navigate('/')}
            data-testid="back-to-dashboard-btn"
            className="text-zinc-400 hover:text-white hover:bg-zinc-900"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {cameFromHeatmap && (
            <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800 border text-[10px] rounded-full font-manrope cursor-pointer hover:text-brand-400 hover:border-brand-500/30 transition-colors"
              onClick={() => navigate('/', { state: { tab: 'resource-heatmap' } })}
              data-testid="back-to-heatmap-badge"
            >
              Back to Heatmap
            </Badge>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-manrope font-bold text-2xl sm:text-3xl tracking-tight text-white" data-testid="estimate-title">
                {isNew ? 'New Estimate' : 'Edit Estimate'}
              </h1>
              <Badge className="bg-brand-500/10 text-brand-400 border-brand-500/20 border font-mono text-xs rounded-full" data-testid="project-id-badge">
                {project.id}
              </Badge>
            </div>
            {duration && (
              <p className="text-zinc-500 text-sm mt-0.5 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Duration: {duration}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="save-project-btn"
            onClick={handleSave}
            disabled={savingLoading}
            className="bg-zinc-900 text-zinc-100 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 font-medium disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {savingLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button
            data-testid="save-and-invoices-btn"
            onClick={handleSaveAndViewInvoices}
            disabled={savingLoading}
            className="bg-brand-500 text-white hover:bg-brand-400 hover:shadow-[0_0_20px_-5px_rgba(168,0,151,0.5)] font-semibold active:scale-95 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {savingLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Save & View Invoices
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Lock Banner for SoW/Execute */}
      {isLockable && (
        <div className={`mb-4 flex items-center justify-between p-3 rounded-lg border ${
          isLocked
            ? 'bg-amber-500/5 border-amber-500/20'
            : 'bg-zinc-900/50 border-zinc-800'
        }`} data-testid="lock-banner">
          <div className="flex items-center gap-2">
            {isLocked ? (
              <>
                <Lock className="w-4 h-4 text-amber-400" />
                <span className="font-manrope text-sm text-amber-300">Resource estimates are locked. Enter passkey to unlock for editing.</span>
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 text-zinc-400" />
                <span className="font-manrope text-sm text-zinc-400">Resource estimates are unlocked. Lock to prevent accidental changes.</span>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openPasskeyModal(isLocked ? 'unlock' : 'lock')}
            data-testid="lock-toggle-btn"
            className={isLocked
              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 text-xs'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800 text-xs'
            }
          >
            {isLocked ? <><Unlock className="w-3.5 h-3.5 mr-1.5" />Unlock</> : <><Lock className="w-3.5 h-3.5 mr-1.5" />Lock Estimates</>}
          </Button>
        </div>
      )}

      {/* Passkey Modal */}
      <Dialog open={showPasskeyModal} onOpenChange={setShowPasskeyModal}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm" data-testid="passkey-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {passkeyAction === 'lock' ? 'Lock Estimates' : 'Unlock Estimates'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-zinc-400 text-sm font-manrope">
              {passkeyAction === 'lock'
                ? 'Enter passkey to lock the estimates and prevent further edits.'
                : 'Enter passkey to unlock the estimates for editing.'}
            </p>
            <Input
              type="password"
              value={passkeyInput}
              onChange={e => { setPasskeyInput(e.target.value); setPasskeyError(''); }}
              onKeyDown={e => e.key === 'Enter' && handlePasskeySubmit()}
              placeholder="Enter passkey"
              data-testid="passkey-input"
              className="bg-zinc-950 border-zinc-800 focus:border-brand-500 font-mono text-sm"
              autoFocus
            />
            {passkeyError && <p className="text-red-400 text-xs font-manrope" data-testid="passkey-error">{passkeyError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPasskeyModal(false)} className="text-zinc-400 hover:text-white">Cancel</Button>
            <Button
              onClick={handlePasskeySubmit}
              data-testid="passkey-submit-btn"
              className="bg-brand-500 text-white hover:bg-brand-400 font-semibold"
            >
              {passkeyAction === 'lock' ? 'Lock' : 'Unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Details */}
      <Card className="bg-zinc-950 border-zinc-900 mb-6" data-testid="project-details-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-manrope font-bold text-base text-white">Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Client Name</Label>
              <Input
                data-testid="client-name-input"
                value={project.clientName}
                onChange={e => updateField('clientName', e.target.value)}
                placeholder="Enter client name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Name</Label>
              <Input
                data-testid="project-name-input"
                value={project.projectName}
                onChange={e => updateField('projectName', e.target.value)}
                placeholder="Enter project name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Lead</Label>
              <Input
                data-testid="project-lead-input"
                value={project.projectLead}
                onChange={e => updateField('projectLead', e.target.value)}
                placeholder="Enter project lead"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Start Date</Label>
              <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="start-date-btn"
                    className={`w-full justify-start text-left font-mono text-sm bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 ${
                      !project.startDate ? 'text-zinc-600' : 'text-zinc-200'
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 text-zinc-500" />
                    {project.startDate ? format(parseISO(project.startDate), 'MMM d, yyyy') : 'Select start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={project.startDate ? parseISO(project.startDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        updateField('startDate', format(date, 'yyyy-MM-dd'));
                        setStartDateOpen(false);
                      }
                    }}
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">End Date</Label>
              <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="end-date-btn"
                    className={`w-full justify-start text-left font-mono text-sm bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700 ${
                      !project.endDate ? 'text-zinc-600' : 'text-zinc-200'
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 text-zinc-500" />
                    {project.endDate ? format(parseISO(project.endDate), 'MMM d, yyyy') : 'Select end date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={project.endDate ? parseISO(project.endDate) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        updateField('endDate', format(date, 'yyyy-MM-dd'));
                        setEndDateOpen(false);
                      }
                    }}
                    disabled={(date) =>
                      project.startDate ? date < parseISO(project.startDate) : false
                    }
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Status</Label>
              <Select value={project.status} onValueChange={(val) => updateField('status', val)}>
                <SelectTrigger data-testid="project-status-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {PROJECT_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Project Type</Label>
              <Select value={project.projectType || 'staff_aug'} onValueChange={(val) => updateField('projectType', val)}>
                <SelectTrigger data-testid="project-type-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {PROJECT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Category</Label>
              <Select value={project.category || 'regular'} onValueChange={(val) => updateField('category', val)}>
                <SelectTrigger data-testid="category-select" className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value} className="text-zinc-200 focus:bg-zinc-900 focus:text-white">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">PO Number</Label>
              <Input
                data-testid="po-number-input"
                value={project.poNumber || ''}
                onChange={e => updateField('poNumber', e.target.value)}
                placeholder="Optional"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 font-manrope text-sm placeholder:text-zinc-600"
              />
              {project.status === 'execute' && !(project.poNumber || '').trim() && (
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    data-testid="dummy-po-checkbox"
                    checked={project.poNumber === '999999'}
                    onCheckedChange={(checked) => updateField('poNumber', checked ? '999999' : '')}
                    className="border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
                  />
                  <span className="text-xs text-amber-400 font-manrope">Assign dummy PO (999999)</span>
                </div>
              )}
            </div>
          </div>

          {duration && (
            <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-500" />
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Duration</span>
                <span className="font-mono text-sm text-white" data-testid="project-duration">{duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-brand-500" />
                <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Estimate</span>
                <span className="font-mono text-sm text-brand-400" data-testid="grand-total">{formatCurrency(grandTotal)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Version Selector for Draft/Proposal */}
      {isVersionable && !isNew && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800" data-testid="version-selector">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Estimate Version:</span>
          <div className="flex items-center gap-1">
            {(project.versions || []).map(v => (
              <Button
                key={v.version}
                size="sm"
                variant={activeVersion === v.version ? 'default' : 'ghost'}
                onClick={() => switchVersion(v.version)}
                data-testid={`version-btn-${v.version}`}
                className={activeVersion === v.version
                  ? 'bg-brand-500 text-white hover:bg-brand-400 text-xs h-7 px-3 font-mono'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800 text-xs h-7 px-3 font-mono'
                }
              >
                V{v.version}
              </Button>
            ))}
            {(project.versions || []).length < 3 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={createNewVersion}
                data-testid="create-version-btn"
                className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs h-7 px-2"
              >
                <Plus className="w-3 h-3 mr-1" />
                V{(project.versions || []).length + 1}
              </Button>
            )}
          </div>
          <span className="text-[10px] text-zinc-600 font-manrope ml-auto">Up to 3 versions allowed</span>
        </div>
      )}

      {/* Version Selection Modal (when moving to SoW/Execute) */}
      <Dialog open={showVersionModal} onOpenChange={(open) => { if (!open) { setShowVersionModal(false); setPendingStatus(null); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm" data-testid="version-select-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">Choose Final Estimate Version</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-zinc-400 text-sm font-manrope">
              Select the version to use as the final estimate. This will be locked and used for Actual vs Planned comparison.
            </p>
            <div className="flex flex-col gap-2">
              {(project.versions || []).map(v => {
                const vTotal = (v.resources || []).reduce((sum, res) => sum + (res.weeklyAllocation || []).reduce((s, w) => s + (w || 0), 0) * (res.rate || 0), 0);
                return (
                  <Button
                    key={v.version}
                    variant="outline"
                    onClick={() => finalizeVersion(v.version)}
                    data-testid={`finalize-version-${v.version}`}
                    className="justify-between bg-zinc-900 border-zinc-800 hover:border-brand-500 hover:bg-zinc-800 text-left h-auto py-3 px-4"
                  >
                    <span className="font-mono text-brand-400 text-sm">V{v.version}</span>
                    <span className="font-mono text-zinc-300 text-sm">{formatCurrency(vTotal)}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resource Capacity Table */}
      <Card className="bg-zinc-950 border-zinc-900" data-testid="resource-table-card">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="font-manrope font-bold text-base text-white">Resource Capacity & Rates</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={addResource}
            data-testid="add-resource-btn"
            disabled={isLocked}
            className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Resource
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {numWeeks > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="sticky left-0 z-10 bg-zinc-950 min-w-[180px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">
                      Resource
                    </TableHead>
                    <TableHead className="min-w-[120px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">
                      Rate ($/wk)
                    </TableHead>
                    <TableHead className="min-w-[80px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">
                      Apply All
                    </TableHead>
                    {weekLabels.map((label, i) => (
                      <TableHead key={i} className="min-w-[90px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">
                        {label}
                      </TableHead>
                    ))}
                    <TableHead className="min-w-[120px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">
                      Total
                    </TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {project.resources.map((res, resIdx) => (
                    <TableRow key={resIdx} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`resource-row-${resIdx}`}>
                      <TableCell className="sticky left-0 z-10 bg-zinc-950 p-2">
                        <Select value={res.type} onValueChange={(val) => changeResourceType(resIdx, val)} disabled={isLocked}>
                          <SelectTrigger
                            data-testid={`resource-type-select-${resIdx}`}
                            className="bg-zinc-950 border-zinc-800 text-xs h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800">
                            {RESOURCE_TYPES.map(r => (
                              <SelectItem key={r.value} value={r.value} className="text-zinc-200 text-xs focus:bg-zinc-900 focus:text-white">
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="p-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-mono">$</span>
                          <Input
                            type="number"
                            data-testid={`resource-rate-${resIdx}`}
                            value={res.rate}
                            onChange={e => updateResource(resIdx, 'rate', Number(e.target.value) || 0)}
                            disabled={isLocked}
                            className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 pl-5 w-24 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="p-2 text-center">
                        <div className="flex items-center justify-center" title="Copy Week 1 to all weeks">
                          <Checkbox
                            data-testid={`apply-all-checkbox-${resIdx}`}
                            checked={!!applyAllWeeks[resIdx]}
                            onCheckedChange={(checked) => toggleApplyAll(resIdx, checked)}
                            disabled={isLocked}
                            className="border-zinc-600 data-[state=checked]:bg-brand-500 data-[state=checked]:border-brand-500"
                          />
                        </div>
                      </TableCell>
                      {(res.weeklyAllocation || []).map((val, weekIdx) => (
                        <TableCell key={weekIdx} className="p-1 text-center">
                          <Input
                            type="number"
                            min="0"
                            data-testid={`allocation-${resIdx}-${weekIdx}`}
                            value={val || ''}
                            onChange={e => updateAllocation(resIdx, weekIdx, e.target.value)}
                            placeholder="0"
                            disabled={isLocked}
                            className="bg-zinc-950 border-zinc-800 font-mono text-xs h-8 w-16 text-center mx-auto focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 placeholder:text-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </TableCell>
                      ))}
                      <TableCell className="p-2 text-right">
                        <span className="font-mono text-sm text-brand-400" data-testid={`resource-total-${resIdx}`}>
                          {formatCurrency(getResourceTotal(res))}
                        </span>
                      </TableCell>
                      <TableCell className="p-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeResource(resIdx)}
                          data-testid={`remove-resource-${resIdx}`}
                          className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          disabled={project.resources.length <= 1 || isLocked}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter className="bg-zinc-950 border-t border-zinc-800">
                  <TableRow className="hover:bg-transparent">
                    <TableCell className="sticky left-0 z-10 bg-zinc-950 font-manrope font-semibold text-xs uppercase tracking-widest text-zinc-400">
                      Weekly Total
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    {Array.from({ length: numWeeks }).map((_, weekIdx) => (
                      <TableCell key={weekIdx} className="text-center">
                        <span className="font-mono text-xs text-zinc-300" data-testid={`week-total-${weekIdx}`}>
                          {formatCurrency(getWeekTotal(weekIdx))}
                        </span>
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <span className="font-mono text-sm font-semibold text-brand-400" data-testid="grand-total-footer">
                        {formatCurrency(grandTotal)}
                      </span>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          ) : (
            <div className="p-8 text-center text-zinc-500 text-sm" data-testid="no-dates-message">
              <CalendarIcon className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>Select start and end dates to configure resource capacity by week</p>
            </div>
          )}
        </CardContent>
        {numWeeks > 0 && (
          <div className="flex justify-end p-4 border-t border-zinc-900">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportExcel}
              data-testid="export-estimate-btn"
              className="text-zinc-400 hover:text-white hover:bg-zinc-900 font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
